import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RefreshCw,
  Sprout,
  Image as ImageIcon,
  MapPin,
  NotebookPen,
  Search,
  X,
  ChevronRight,
  ExternalLink,
} from 'lucide-react';
import { API_BASE_URL } from '../config';

function formatDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
}

function accountKindBadge(kind) {
  const map = {
    customer: { label: 'Gardener', cls: 'bg-green-100 text-green-800' },
    user: { label: 'Gardener', cls: 'bg-green-100 text-green-800' },
    designer: { label: 'Designer', cls: 'bg-purple-100 text-purple-700' },
    admin: { label: 'Admin', cls: 'bg-amber-100 text-amber-800' },
    unknown: { label: 'Unknown', cls: 'bg-gray-100 text-gray-700' },
  };
  const entry = map[kind] || map.unknown;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${entry.cls}`}>
      {entry.label}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, color = 'text-green-700' }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-full bg-green-50 flex items-center justify-center ${color}`}>
        <Icon size={20} />
      </div>
      <div>
        <div className="text-2xl font-semibold text-gray-900 leading-none">{value ?? 0}</div>
        <div className="text-xs text-gray-500 mt-1">{label}</div>
      </div>
    </div>
  );
}

function GardenerDetailDrawer({ gardener, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!gardener?.user_id) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('adminToken');
    fetch(`${API_BASE_URL}/admin/garden-users/${gardener.user_id}/plants`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setError(data.error || 'Failed to load detail');
        } else {
          setDetail(data);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Network error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [gardener?.user_id]);

  if (!gardener) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative ml-auto h-full w-full max-w-2xl bg-white shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-start justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                {gardener.full_name || gardener.email || 'Gardener'}
              </h3>
              {accountKindBadge(gardener.account_kind)}
            </div>
            <div className="text-sm text-gray-500 truncate">{gardener.email}</div>
            <div className="text-xs text-gray-400 mt-1">
              Joined {formatDate(gardener.account_created_at)} · Last activity {formatDate(gardener.last_activity_at)}
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-4 p-2 -m-2 text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-6">
          {loading && (
            <div className="py-10 text-center text-sm text-gray-500">Loading plants…</div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {detail && (
            <>
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Zones ({detail.zones.length})
                </h4>
                {detail.zones.length === 0 ? (
                  <p className="text-sm text-gray-500">No zones yet.</p>
                ) : (
                  <ul className="grid grid-cols-2 gap-3">
                    {detail.zones.map((z) => (
                      <li
                        key={z.id}
                        className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                      >
                        <div className="font-medium text-sm text-gray-900 truncate">{z.name}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {z.marker_count} marker{z.marker_count === 1 ? '' : 's'} · created {formatDate(z.created_at)}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Plants ({detail.plants.length})
                </h4>
                {detail.plants.length === 0 ? (
                  <p className="text-sm text-gray-500">No plants uploaded yet.</p>
                ) : (
                  <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
                    {detail.plants.map((p) => (
                      <li key={p.id} className="flex items-center gap-3 p-3 bg-white">
                        {p.thumbnail_url ? (
                          // eslint-disable-next-line jsx-a11y/alt-text
                          <img
                            src={p.thumbnail_url}
                            alt=""
                            className="w-12 h-12 rounded-md object-cover flex-shrink-0 bg-gray-100"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-md bg-green-50 text-green-600 flex items-center justify-center flex-shrink-0">
                            <Sprout size={18} />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {p.common_name || p.nickname || p.botanical_name || 'Unnamed plant'}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {p.botanical_name && p.botanical_name !== p.common_name ? p.botanical_name : null}
                            {p.botanical_name && p.location_notes ? ' · ' : ''}
                            {p.location_notes || ''}
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            Added {formatDate(p.created_at)} · {p.photo_count} photo{p.photo_count === 1 ? '' : 's'}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminGardeners() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('plant_count'); // 'plant_count' | 'last_activity_at' | 'account_created_at'
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/admin/garden-users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to load');
      }
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotals(data.totals || null);
    } catch (err) {
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? rows.filter((r) => {
          return (
            (r.email || '').toLowerCase().includes(q) ||
            (r.full_name || '').toLowerCase().includes(q)
          );
        })
      : rows.slice();

    base.sort((a, b) => {
      if (sortKey === 'plant_count') {
        return (b.plant_count || 0) - (a.plant_count || 0);
      }
      if (sortKey === 'account_created_at') {
        return new Date(b.account_created_at || 0) - new Date(a.account_created_at || 0);
      }
      return new Date(b.last_activity_at || 0) - new Date(a.last_activity_at || 0);
    });
    return base;
  }, [rows, search, sortKey]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sprout className="text-green-700" size={24} />
            Gardeners
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Everyone using the My Garden feature, with their plant, zone, and
            photo counts. Click a row to see the plants they&rsquo;ve added.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="px-4 py-2 bg-green-700 hover:bg-green-800 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Top line numbers */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard icon={Sprout} label="Active gardeners" value={totals?.gardeners} />
        <StatCard icon={Sprout} label="Plants uploaded" value={totals?.plants} />
        <StatCard icon={MapPin} label="Zones created" value={totals?.zones} />
        <StatCard icon={ImageIcon} label="Photos" value={totals?.photos} />
        <StatCard icon={NotebookPen} label="Journal entries" value={totals?.journal} />
      </div>

      {/* Search + sort */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-green-600"
          />
        </div>
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-green-600"
        >
          <option value="plant_count">Sort: Most plants</option>
          <option value="last_activity_at">Sort: Most recent activity</option>
          <option value="account_created_at">Sort: Newest accounts</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm mb-4">
          {error}
        </div>
      )}

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Gardener</th>
              <th className="text-left px-4 py-2 font-medium">Type</th>
              <th className="text-right px-4 py-2 font-medium">Plants</th>
              <th className="text-right px-4 py-2 font-medium">Zones</th>
              <th className="text-right px-4 py-2 font-medium">Photos</th>
              <th className="text-right px-4 py-2 font-medium">Journal</th>
              <th className="text-left px-4 py-2 font-medium">Last activity</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading && filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">
                  Loading gardeners…
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">
                  {rows.length === 0
                    ? 'No one has used the My Garden feature yet.'
                    : 'No gardeners match your search.'}
                </td>
              </tr>
            )}
            {filtered.map((r) => (
              <tr
                key={r.user_id}
                onClick={() => setSelected(r)}
                className="hover:bg-green-50/50 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900 truncate">
                    {r.full_name || '—'}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{r.email || '—'}</div>
                </td>
                <td className="px-4 py-3">{accountKindBadge(r.account_kind)}</td>
                <td className="px-4 py-3 text-right font-semibold text-gray-900">
                  {r.plant_count}
                </td>
                <td className="px-4 py-3 text-right text-gray-700">{r.zone_count}</td>
                <td className="px-4 py-3 text-right text-gray-700">{r.photo_count}</td>
                <td className="px-4 py-3 text-right text-gray-700">{r.journal_count}</td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                  {formatDate(r.last_activity_at)}
                </td>
                <td className="px-2 py-3 text-gray-300">
                  <ChevronRight size={16} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totals && rows.length > 0 && (
        <p className="mt-4 text-xs text-gray-500 flex items-center gap-1">
          <ExternalLink size={12} />
          Click any row to see plants and zones for that gardener.
        </p>
      )}

      {selected && (
        <GardenerDetailDrawer gardener={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
